import { useCallback, useEffect, useState } from "react";
import {
  CheckCircle, Warning, Trash, Key, ArrowsClockwise, Power, Globe,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";

const SOURCE_STYLES = {
  admin_override: "bg-emerald-500/20 text-emerald-300",
  env: "bg-amber-500/20 text-amber-200",
  not_configured: "bg-slate-500/30 text-slate-300",
};

/**
 * Iter 20.2 — Adhoc Brave query tester. Independent of any operator
 * config. Proves the key + parser are healthy.
 */
function BraveTestQueryButton({ disabled, testId }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("site:botb.com competition");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post(
        "/admin/providers/brave_search/test-query",
        { query, limit: 5 },
      );
      setResult(data);
      if (data.ok) toast.success(`Brave returned ${data.raw_count} raw results.`);
      else toast.error("Brave: " + (data.error || "unknown error"));
    } catch (e) {
      toast.error("Test query failed: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <div className="w-full">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        data-testid={testId}
        className="px-3 py-1.5 text-xs font-semibold rounded bg-white/10 hover:bg-white/15 text-white inline-flex items-center gap-1.5 disabled:opacity-50"
      >
        <Globe size={12} /> {open ? "Hide" : "Test query"}
      </button>
      {open && (
        <div className="mt-3 rounded border border-white/10 bg-black/30 p-3 space-y-2" data-testid={`${testId}-panel`}>
          <label className="block">
            <div className="overline text-white/50 mb-1.5">Adhoc query (no operator scope)</div>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid={`${testId}-input`}
              className="w-full bg-white/5 border border-white/15 rounded px-3 py-1.5 text-xs outline-none focus:border-[var(--cc-gold)] font-mono-num"
            />
          </label>
          <button
            type="button"
            onClick={run}
            disabled={busy || disabled}
            data-testid={`${testId}-run`}
            className="px-3 py-1.5 text-xs font-semibold rounded bg-sky-500/30 hover:bg-sky-500/40 text-sky-100 disabled:opacity-50"
          >
            {busy ? "Querying…" : "Run query"}
          </button>
          {result && (
            <div className="mt-2 text-[11px] text-white/70 space-y-1" data-testid={`${testId}-result`}>
              <div>
                <span className="opacity-60">raw_count:</span>{" "}
                <span className="font-mono-num text-white">{result.raw_count}</span>
                {result.error && (
                  <span className="ml-2 text-red-300">error: {result.error}</span>
                )}
              </div>
              {(result.preview || []).map((p, i) => (
                <div key={i} className="truncate" title={p.title}>
                  · #{p.rank} <span className="text-white/85">{p.title}</span>{" "}
                  <span className="text-white/45">[{p.host}]</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Generic provider key block — used for both Brave Search and OpenAI
 * (gpt-4o-mini classifier). Encrypted storage, admin override → env var
 * fallback. Raw key is never returned by the backend.
 */
function ProviderBlock({
  providerKey,
  label,
  envVarName,
  keyPlaceholder,
  keyMinLength,
  fallbackNote,
}) {
  const [state, setState] = useState(null);
  const [newKey, setNewKey] = useState("");
  const [busy, setBusy] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/providers/${providerKey}`);
      setState(data);
    } catch (e) {
      toast.error(`Failed to load ${label} settings`);
    }
  }, [label, providerKey]);
  useEffect(() => { refresh(); }, [refresh]);

  const save = async (e) => {
    e.preventDefault();
    if (!newKey.trim()) return;
    setBusy("save");
    try {
      await api.put(`/admin/providers/${providerKey}`, { api_key: newKey.trim() });
      toast.success(`${label} override saved.`);
      setNewKey("");
      refresh();
    } catch (e2) {
      toast.error("Save failed: " + (e2.response?.data?.detail || e2.message));
    } finally { setBusy(null); }
  };

  const test = async () => {
    setBusy("test");
    try {
      const { data } = await api.post(`/admin/providers/${providerKey}/test`, {});
      if (data.ok) toast.success(`${label} key OK.`);
      else toast.error(`${label} test failed: ${data.error || data.detail || "unknown"}`);
      refresh();
    } catch (e) {
      toast.error("Test failed");
    } finally { setBusy(null); }
  };

  const disable = async () => {
    setBusy("disable");
    try {
      await api.post(`/admin/providers/${providerKey}/disable-override`);
      toast.success(`${label} override disabled — falling back to env var.`);
      refresh();
    } catch (e) { toast.error("Disable failed"); } finally { setBusy(null); }
  };

  const remove = async () => {
    if (!window.confirm(`Remove the stored admin override key? Production will fall back to ${envVarName} env var if set.`)) return;
    setBusy("remove");
    try {
      await api.delete(`/admin/providers/${providerKey}`);
      toast.success("Override removed.");
      refresh();
    } catch (e) { toast.error("Remove failed"); } finally { setBusy(null); }
  };

  if (!state) return <div className="text-white/60">Loading {label} settings…</div>;

  const stored = state.stored_override;
  const testId = providerKey.replace(/_/g, "-");
  return (
    <div className="space-y-4" data-testid={`provider-block-${testId}`}>
      <div className="rounded-lg border border-white/10 bg-white/[0.02] p-5">
        <div className="overline text-[var(--cc-gold)] mb-3 inline-flex items-center gap-2"><Key size={14} /> {label}</div>
        {stored?.last_error_kind === "provider_limit_reached" && (
          <div
            className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 mb-3 text-[12px] text-red-200 flex items-start gap-2"
            data-testid={`${testId}-limit-banner`}
          >
            <Warning size={14} weight="fill" className="mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold">Brave Search limit reached</div>
              <div className="text-[11px] text-red-200/85 mt-0.5">
                {stored.last_error_summary || "Check Brave billing or credits."}
              </div>
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-y-2 text-sm">
          <div className="text-white/50">Active source</div>
          <div data-testid={`${testId}-active-source`}>
            <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-semibold ${SOURCE_STYLES[state.active_source] || ""}`}>
              {state.active_source}
            </span>
          </div>
          <div className="text-white/50">Active key preview</div>
          <div className="font-mono-num" data-testid={`${testId}-masked-preview`}>{state.active_masked_preview || "—"}</div>
          <div className="text-white/50">Env var present</div>
          <div>{state.env_var_present ? "yes" : "no"}</div>
          {stored && (
            <>
              <div className="text-white/50">Stored override</div>
              <div>
                <span className="font-mono-num text-xs">{stored.masked_key_preview}</span>
                {" · "}
                <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase font-semibold ${stored.last_status === "ok" ? "bg-emerald-500/20 text-emerald-300" : "bg-red-500/20 text-red-300"}`}>
                  {stored.last_status || "unverified"}
                </span>
                {stored.last_error_summary && (
                  <span className="text-[11px] text-red-300 ml-2">{stored.last_error_summary}</span>
                )}
              </div>
              <div className="text-white/50">Updated by</div>
              <div className="font-mono-num text-xs">{stored.updated_by || "—"}</div>
              <div className="text-white/50">Verified at</div>
              <div className="text-xs text-white/70">{stored.last_verified_at ? new Date(stored.last_verified_at).toLocaleString("en-GB") : "—"}</div>
            </>
          )}
        </div>
        <div className="flex gap-2 mt-3 flex-wrap">
          <button type="button" onClick={test} disabled={busy === "test" || state.active_source === "not_configured"} data-testid={`${testId}-test-btn`} className="px-3 py-1.5 text-xs font-semibold rounded bg-white/10 hover:bg-white/15 text-white inline-flex items-center gap-1.5 disabled:opacity-50">
            <ArrowsClockwise size={12} /> Test connection
          </button>
          {providerKey === "brave_search" && (
            <BraveTestQueryButton
              disabled={state.active_source === "not_configured"}
              testId={`${testId}-test-query-btn`}
            />
          )}
          {stored && stored.enabled && (
            <button type="button" onClick={disable} disabled={busy === "disable"} data-testid={`${testId}-disable-btn`} className="px-3 py-1.5 text-xs font-semibold rounded bg-amber-500/10 border border-amber-500/40 text-amber-200 inline-flex items-center gap-1.5 disabled:opacity-50">
              <Power size={12} /> Disable override
            </button>
          )}
          {stored && (
            <button type="button" onClick={remove} disabled={busy === "remove"} data-testid={`${testId}-remove-btn`} className="px-3 py-1.5 text-xs font-semibold rounded bg-red-500/10 border border-red-500/40 text-red-300 inline-flex items-center gap-1.5 disabled:opacity-50">
              <Trash size={12} /> Remove override
            </button>
          )}
        </div>
      </div>

      <form onSubmit={save} className="rounded-lg border border-white/10 bg-white/[0.02] p-5 space-y-3" data-testid={`${testId}-set-key-form`}>
        <div className="overline text-[var(--cc-gold)] mb-2">Set / update {label} override</div>
        <p className="text-[11px] text-white/50 leading-relaxed">
          Pasting a new key replaces any existing override. The raw value is encrypted at rest, never shown again, and never appears in API responses or logs.
        </p>
        <label className="block">
          <div className="overline text-white/50 mb-1.5">{label} API key</div>
          <input
            type="password"
            value={newKey}
            onChange={(e) => setNewKey(e.target.value)}
            placeholder={keyPlaceholder}
            data-testid={`${testId}-new-key-input`}
            className="w-full bg-white/5 border border-white/15 rounded px-3 py-2 text-sm outline-none focus:border-[var(--cc-gold)] font-mono-num"
            minLength={keyMinLength}
          />
        </label>
        <button type="submit" disabled={busy === "save" || newKey.length < keyMinLength} data-testid={`${testId}-save-btn`} className="btn-gold px-4 py-2 rounded text-sm inline-flex items-center gap-1.5 disabled:opacity-50">
          <CheckCircle size={14} weight="bold" /> {busy === "save" ? "Saving…" : "Save override"}
        </button>
      </form>

      <div className="rounded-md border border-white/10 bg-white/[0.02] p-3 text-[11px] text-white/60 flex gap-2 items-start">
        <Warning size={14} className="mt-0.5 text-amber-300" />
        <div>
          Resolver priority: <strong className="text-white">admin override</strong> → <strong className="text-white">Render env var ({envVarName})</strong> → not configured.
          {fallbackNote}
        </div>
      </div>
    </div>
  );
}

export default function ProviderSettings() {
  return (
    <div className="space-y-8 max-w-3xl" data-testid="admin-provider-settings">
      <ProviderBlock
        providerKey="brave_search"
        label="Brave Search"
        envVarName="BRAVE_SEARCH_API_KEY"
        keyPlaceholder="BSA…"
        keyMinLength={8}
        fallbackNote={
          <> Brave discovery never auto-publishes; every candidate lands in <code className="text-[var(--cc-gold)]">source_pending_review</code> or lower until you promote it.</>
        }
      />
      <ProviderBlock
        providerKey="openai_classifier"
        label="OpenAI (gpt-4o-mini classifier)"
        envVarName="OPENAI_API_KEY"
        keyPlaceholder="sk-…"
        keyMinLength={16}
        fallbackNote={
          <> When no key is configured, the candidate classifier runs in <strong className="text-white">deterministic-only</strong> mode — regex / keyword rules over URL, title and snippet. The LLM is only used to refine uncertain candidates and never auto-promotes anything to verified_live.</>
        }
      />
    </div>
  );
}
